
import { useEffect, useMemo } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import routes from '../../router/config';

export default function ContactPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = 'Contact Cape Town Professional Painters | Free Quotes & Consultations | Atlantic Beach Estate Big Bay Bloubergstrand';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contact Cape Town\'s leading painting contractors for free quotes and consultations. Serving Atlantic Beach Estate, Big Bay, Bloubergstrand, Bothasig, Camps Bay. Call now for expert painting services.');
    }

    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'contact painters Cape Town, free painting quotes, painting consultation, Cape Town painting contractors');
    }

    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`);
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact Cape Town Professional Painters",
      "description": "Contact Cape Town's leading painting contractors for free quotes and consultations.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Professional Painters",
        "telephone": "+27123456789",
        "email": "Get a quote",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Cape Town",
          "addressRegion": "Western Cape",
          "addressCountry": "ZA"
        }
      }
    });
    document.head.appendChild(script);

    // Load Elfsight script
    const elfsightScript = document.createElement('script');
    elfsightScript.src = 'https://elfsightcdn.com/platform.js';
    elfsightScript.async = true;
    document.head.appendChild(elfsightScript);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('ContactPage')) {
        existingScript.remove();
      }
      const elfsightScriptElement = document.querySelector('script[src="https://elfsightcdn.com/platform.js"]');
      if (elfsightScriptElement) {
        elfsightScriptElement.remove();
      }
    };
  }, []);

  // Automatically generate area pages from routes
  const areaPages = useMemo(() => {
    return routes
      .filter(route => route.path?.startsWith('/areas/'))
      .map(route => {
        const path = route.path!;
        const name = path
          .replace('/areas/', '')
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        return { name, href: path };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/dc0773882880ecdfeb02d087cf27c611.jpg"
            alt="Professional painting consultation in Cape Town"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Get Your <span className="text-yellow-400">Free Quote</span> Today
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Ready to transform your property? Contact Cape Town's most trusted painting contractors for a comprehensive consultation and detailed quote.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Contact Form - Now first on mobile */}
            <div className="bg-gray-900 rounded-2xl shadow-xl p-8 border border-gray-700 order-1 lg:order-2">
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">Request Your Free Quote</h3>
                <p className="text-gray-300">Fill out the form below to get a quote from the best painters near you.</p>
              </div>
              
              {/* Elfsight Form Builder */}
              <div className="elfsight-app-b2582d3f-f230-4eb0-aabc-eec0745fba11" data-elfsight-app-lazy></div>
            </div>

            {/* Contact Information - Now second on mobile */}
            <div className="space-y-8 order-2 lg:order-1">
              <div>
                <h2 className="text-4xl font-bold text-black mb-6">
                  Let's Discuss Your Painting Project
                </h2>
                <p className="text-xl text-gray-600 leading-relaxed mb-8">
                  Whether you're planning a single room refresh or a complete property transformation, our experienced team is here to help. We provide detailed quotes, professional consultations, and expert advice for all your painting needs across Cape Town's most prestigious areas.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed mb-8">
                  Our team of certified painting professionals has been serving Cape Town homeowners and businesses for over 15 years. We understand the unique challenges of coastal painting, from salt air corrosion to UV protection, and we use only premium materials designed to withstand South Africa's diverse climate conditions.
                </p>
              </div>

              {/* Contact Details */}
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#ffff00'}}>
                    <i className="ri-map-pin-line text-xl text-black"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-black">Service Areas</h3>
                    <p className="text-gray-600">Cape Town & Surrounding Areas</p>
                    <p className="text-sm text-gray-500">Free quotes</p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#ffff00'}}>
                    <i className="ri-time-line text-xl text-black"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-black">Business Hours</h3>
                    <p className="text-gray-600">Mon-Fri: 7:30-17:00, Sat: 8:00-14:00</p>
                    <p className="text-sm text-gray-500">Emergency services available 24/7</p>
                  </div>
                </div>
              </div>

              {/* Why Choose Us */}
              <div className="bg-gray-50 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-black mb-6">Why Choose Our Services?</h3>
                <div className="space-y-4">
                  {[
                    'Free detailed quotes and on-site consultations',
                    'Over 15 years of professional painting experience',
                    'Premium quality materials and eco-friendly options',
                    '100% satisfaction promise on all completed work',
                    'Competitive pricing with transparent, no hidden costs',
                    'Specialized coastal and marine-grade protection',
                    'Color consultation and design advisory services'
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <i className="ri-check-double-line text-green-500 text-lg"></i>
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Service Promise */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-black mb-4">Our Platform Promise</h3>
                <p className="text-gray-700 leading-relaxed">
                  We guarantee you'll be connected with only the most qualified and reliable painting contractors in Cape Town. Every contractor in our network is thoroughly vetted, fully licensed, and committed to delivering exceptional results. Your satisfaction with both our platform service and your chosen contractor is our top priority.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Some Areas We Serve Across Cape Town
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide professional painting services across Cape Town's most prestigious neighborhoods and surrounding areas. Our local expertise ensures we understand the unique requirements of each location.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {areaPages.map((area, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow border border-gray-100"
              >
                <i className="ri-map-pin-2-line text-3xl text-black mb-3 w-12 h-12 flex items-center justify-center mx-auto" style={{backgroundColor: '#ffff00', borderRadius: '50%'}}></i>
                <h3 className="font-semibold text-black text-sm">{area.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Get answers to common questions about our platform and services
            </p>
          </div>

          <div className="space-y-8">
            {[
              {
                question: "How does Cape Town Painters platform work?",
                answer: "Simply submit your project details through our platform, and we'll connect you with up to 5 vetted premium painting contractors. You'll receive detailed quotes to compare and choose the best fit for your project."
              },
              {
                question: "How many quotes will I receive?",
                answer: "You can receive up to 5 detailed quotes from our network of premium painting contractors. This allows you to compare pricing, timelines, and approaches to find the perfect match for your project."
              },
              {
                question: "Are all contractors on your platform vetted?",
                answer: "Absolutely. Every contractor in our network undergoes rigorous vetting including license verification, insurance checks, quality assessments, and customer review analysis. We only work with Cape Town's most trusted professionals."
              },
              {
                question: "Is there a cost to use Cape Town Painters platform?",
                answer: "No, our platform is completely free for property owners. You pay nothing to receive quotes and connect with contractors. You only pay the contractor you choose directly for their services."
              },
              {
                question: "What areas do you cover in Cape Town?",
                answer: "We connect you with premium contractors across all major Cape Town areas including Camps Bay, Clifton, Sea Point, Green Point, Waterfront, Constantia, Newlands, Claremont, and surrounding suburbs."
              },
              {
                question: "How quickly will I receive quotes?",
                answer: "Most customers receive their first quotes within 24-48 hours of submitting their project. All quotes are typically received within 3-5 business days, depending on project complexity."
              },
              {
                question: "What types of painting projects do you handle?",
                answer: "Our contractor network handles all types of painting projects including residential interior and exterior painting, commercial properties, heritage restorations, waterproofing, and specialized finishes."
              },
              {
                question: "Can I choose my own contractor from the quotes?",
                answer: "Yes, you have complete control. Review all quotes, compare contractor profiles, read reviews, and choose the contractor that best fits your needs, budget, and timeline."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-8">
                <h3 className="text-xl font-semibold text-black mb-4">
                  {faq.question}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="py-16 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-black mb-4">
            Ready to Connect with Premium Painters?
          </h2>
          <p className="text-xl text-black/80 mb-8">
            Get up to 5 detailed quotes from Cape Town's most trusted painting contractors. Start your project today with our premium platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-6">
            <a
              href="/contact"
              className="bg-teal-600 text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
